import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  HStack,
  Spacer,
  Stack,
  StackSeparator,
  Text,
  Box,
  Popover,
  VStack,
  Checkbox,
  useDisclosure,
  Link,
  Button,
} from "@chakra-ui/react";
import { timeAgo } from "@keita/lanepatrol-sdk";
import { t } from "i18next";
import { AccountRole, AccountUser } from "../../models/AccountUser";
import { useStores } from "../../../shared/stores/RootStore";
import { ChevronDownIcon } from "../../../shared/views/icons";
import { CustomPopover } from "../../../shared/views/CustomPopover";
import CustomAvatar from "../../../shared/views/CustomAvatar";
import { toast } from "../../../shared/views/CustomToaster";

interface Props {
  accountUser: AccountUser;
  me: boolean;
}
const AccountUserItemBase: React.FC<Props> = ({ accountUser, me }) => {
  const { accountStore } = useStores();

  const popoverState = useDisclosure();
  const [roles, setRoles] = useState<Set<AccountRole>>(new Set());

  useEffect(() => {
    setRoles(new Set(accountUser.roles));
  }, [accountUser]);

  const changeRole = (checked: boolean, name: string) => {
    setRoles((prev) => {
      if (checked) {
        if (name === AccountRole.Admin) {
          prev.clear();
        }
        prev.add(name as AccountRole);
      } else {
        prev.delete(name as AccountRole);
      }

      return new Set(prev);
    });
  };

  const onClose = () => {
    var notDirty =
      accountUser.roles.length === roles.size &&
      accountUser.roles.every((role) => roles.has(role));
    if (!notDirty) {
      accountStore.updateAccountUser(accountUser.id, {
        roles: [...roles],
      });
      toast("success", "Roles updated");
    }
    popoverState.onClose();
  };

  const onRemove = () => {
    accountStore.deleteAccountUser(accountUser.id);
    popoverState.onClose();
  };

  return (
    <>
      <HStack p={2} _hover={{ bgColor: "gray.50" }}>
        <CustomAvatar size="sm" name={accountUser.email} />

        <Stack gap={0}>
          <Text fontSize="sm">{accountUser.email}</Text>
          <Text fontSize="xs">
            Added {timeAgo(new Date(accountUser.createdAt))}
          </Text>
        </Stack>
        <Spacer />

        <CustomPopover
          open={popoverState.open}
          onOpen={popoverState.onOpen}
          onClose={onClose}
          trigger={
            <Button disabled={me} variant="ghost">
              <ChevronDownIcon /> {formatRoles(accountUser.roles)}
            </Button>
          }
        >
          <Popover.Body>
            <VStack alignItems="start" separator={<StackSeparator />}>
              {Object.values(AccountRole).map((role, i) => (
                <Checkbox.Root
                  checked={roles.has(role) || roles.has(AccountRole.Admin)}
                  disabled={
                    roles.has(AccountRole.Admin) && role !== AccountRole.Admin
                  }
                  onCheckedChange={({ checked }) => {
                    changeRole(!!checked, role);
                  }}
                  colorPalette="primary"
                  display="flex"
                  pl={2}
                  key={i}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>
                    <Box ml={1} flexGrow={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {t(`account:Users.roles.${role}`)}
                      </Text>
                      <Text fontSize="xs">
                        {t(`account:Users.roleDescriptions.${role}`)}
                      </Text>
                    </Box>
                  </Checkbox.Label>
                </Checkbox.Root>
              ))}
              <Link fontSize="sm" fontWeight="semibold" onClick={onRemove}>
                {t(`account:Users.revokeButton`)}
              </Link>
            </VStack>
          </Popover.Body>
        </CustomPopover>
      </HStack>
    </>
  );
};

export const AccountUserItem = observer(AccountUserItemBase);

const formatRoles = (roles: AccountRole[]) =>
  roles.map((role) => t(`account:Users.roles.${role}`)).join(", ") ||
  t(`account:Users.rolePlaceholder`);
